// Création rapide depuis un créneau vide du calendrier (ou de l'agenda d'un
// dossier) : rendez-vous, audience ou tâche, sans quitter la page.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '../../lib/cn';
import { useAuthStore } from '../../store/useAuthStore';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useCreateAppointment } from '../../hooks/queries/useAppointments';
import { useCreateTask } from '../../hooks/queries/useTasks';
import { EventTypeSelect } from './EventTypeSelect';
import { createDossierHearing } from '../../services/dossiers/hearingService';
import { ficheToInstant } from '../../lib/appointments/appointmentDraft';
import { PersonSelect } from '../ui/PersonSelect';
import { Segments } from '../ui/ListToolbar';

export type QuickCreateKind = 'appointment' | 'hearing' | 'task';

export interface QuickCreateSlot {
  /** Jour visé, AAAA-MM-JJ. */
  day: string;
  /** Heure visée, HH:MM. Absente pour un clic sur une case du mois. */
  time: string | null;
  /** Point d'ancrage à l'écran. */
  x: number;
  y: number;
}

interface Props {
  slot: QuickCreateSlot;
  /** Dossier imposé (agenda d'un dossier). */
  fixedDossier?: { id: string; title: string; number: string | null } | null;
  onClose: () => void;
  /** « Plus d'options » : ouvre la fiche complète du type choisi. */
  onMore: (kind: QuickCreateKind) => void;
}

const KIND_LABEL: Record<QuickCreateKind, string> = {
  appointment: 'Rendez-vous',
  hearing: 'Audience',
  task: 'Tâche',
};

const WIDTH = 380;

function plusOneHour(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const total = Math.min((h ?? 9) * 60 + (m ?? 0) + 60, 23 * 60 + 45);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function CalendarQuickCreate({ slot, fixedDossier, onClose, onMore }: Props) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.collaborator);
  const canAgenda = useCanAccessFeature('hearings_create');
  const canTask = useCanAccessFeature('tasks_create');
  const kinds = useMemo<QuickCreateKind[]>(
    () => [...(canAgenda ? (['appointment', 'hearing'] as const) : []), ...(canTask ? (['task'] as const) : [])],
    [canAgenda, canTask],
  );
  const [kind, setKind] = useState<QuickCreateKind>(kinds[0] ?? 'task');
  const [title, setTitle] = useState('');
  const [day, setDay] = useState(slot.day);
  const [start, setStart] = useState(slot.time ?? '09:00');
  const [end, setEnd] = useState(plusOneHour(slot.time ?? '09:00'));
  const [dossierId, setDossierId] = useState(fixedDossier?.id ?? '');
  const [people, setPeople] = useState<string[]>(me?.id ? [me.id] : []);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { data: dossiers } = useDossiers({ include_closed: false });
  const createAppointment = useCreateAppointment();
  const createTask = useCreateTask();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      // Les listes déroulantes (personnes) vivent dans un portail hors du panneau.
      if (panelRef.current?.contains(target) || target.closest('[data-radix-popper-content-wrapper]')) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  if (kinds.length === 0) return null;

  const needsDossier = kind === 'hearing';
  const timed = kind !== 'task' || slot.time !== null;
  const startsAt = ficheToInstant(day, start);
  const endsAt = ficheToInstant(day, end);
  const rangeError =
    kind !== 'task' && startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)
      ? 'La fin doit être après le début.'
      : null;
  const missing = !title.trim()
    ? 'Ajoutez un titre.'
    : !day
      ? 'Choisissez un jour.'
      : needsDossier && !dossierId
        ? 'Une audience exige un dossier.'
        : kind === 'appointment' && people.length === 0
          ? 'Choisissez au moins un intervenant.'
          : rangeError;

  async function save() {
    if (missing || saving) return;
    setSaving(true);
    try {
      if (kind === 'appointment') {
        if (!startsAt || !endsAt) return;
        await createAppointment.mutateAsync({
          title: title.trim(),
          starts_at: startsAt,
          ends_at: endsAt,
          dossier_id: dossierId || null,
          event_type_id: typeId,
          participantIds: people,
        });
      } else if (kind === 'hearing') {
        if (!startsAt) return;
        await createDossierHearing({
          dossier_id: dossierId,
          label: title.trim(),
          description: null,
          court: null,
          starts_at: startsAt,
          ends_at: endsAt,
          notes: null,
          owner_id: people[0] ?? me?.id ?? null,
          event_type_id: typeId,
          people: people.map((id) => ({ kind: 'collaborator' as const, id })),
          created_by: me?.id ?? null,
        });
        qc.invalidateQueries({ queryKey: ['dossiers'] });
        qc.invalidateQueries({ queryKey: ['hearing-attendees'] });
      } else {
        await createTask.mutateAsync({
          content: title.trim(),
          priority: 3,
          due_date: day,
          due_datetime: timed ? startsAt : null,
          dossier_id: dossierId || null,
          section_id: null,
          assigneeIds: people,
        });
      }
      // Le calendrier et l'agenda du dossier relisent leurs sources tout de suite.
      await qc.invalidateQueries({ queryKey: ['calendar'] });
      toast.success(
        `${KIND_LABEL[kind]} enregistré${kind === 'appointment' ? '' : 'e'}. ${
          dossierId ? 'Visible aussi dans le dossier.' : 'Visible uniquement dans le calendrier.'
        }`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer');
    } finally {
      setSaving(false);
    }
  }

  const left = Math.max(12, Math.min(slot.x, window.innerWidth - WIDTH - 12));
  const top = Math.max(12, Math.min(slot.y, window.innerHeight - 400));
  const field =
    'h-8 rounded-md border border-neutral-200 bg-white px-2 text-[13px] text-cime-text-primary focus:border-cime-primary focus:outline-none';

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Ajouter au calendrier"
      data-cime-modal
      className="fixed z-[90] rounded-xl border border-neutral-200 bg-white shadow-[0_16px_40px_rgba(4,52,41,0.18)]"
      style={{ left, top, width: WIDTH }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-3 py-2.5">
        <Segments
          label="Type"
          value={kind}
          onChange={(next) => {
            setKind(next);
            setTypeId(null);
          }}
          options={kinds.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="grid h-7 w-7 place-items-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-cime-text-primary"
        >
          <img src="/icons/xmark.svg" alt="" className="h-3 w-3 opacity-60" />
        </button>
      </div>

      <div className="flex flex-col gap-2.5 px-3.5 py-3">
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void save();
          }}
          placeholder="Ajouter un titre"
          aria-label="Titre"
          autoComplete="off"
          className="h-9 border-0 border-b border-neutral-200 bg-transparent px-0 text-[15px] font-medium text-cime-text-primary placeholder:font-normal placeholder:text-neutral-400 focus:border-cime-primary focus:outline-none"
        />
        {kind !== 'task' ? (
          <EventTypeSelect
            family={kind}
            value={typeId}
            onChange={(id, type) => {
              setTypeId(id);
              // Une audience sans titre prend le nom de son type.
              if (type && kind === 'hearing' && !title.trim()) setTitle(type.name);
            }}
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Jour" className={field} />
          {timed ? (
            <input
              type="time"
              step={900}
              value={start}
              onChange={(e) => {
                setStart(e.target.value);
                setEnd(plusOneHour(e.target.value));
              }}
              aria-label="Début"
              className={field}
            />
          ) : null}
          {kind !== 'task' ? (
            <>
              <span className="text-neutral-400">→</span>
              <input type="time" step={900} value={end} onChange={(e) => setEnd(e.target.value)} aria-label="Fin" className={field} />
            </>
          ) : null}
        </div>
        {fixedDossier ? (
          <p className="truncate text-[13px] text-cime-text-primary">
            {fixedDossier.number ? <small className="mr-1.5 text-[11px] text-neutral-400">{fixedDossier.number}</small> : null}
            {fixedDossier.title}
          </p>
        ) : (
          <select
            value={dossierId}
            onChange={(e) => setDossierId(e.target.value)}
            aria-label="Dossier"
            className={cn(field, 'w-full', needsDossier && !dossierId && 'border-[#c8431f]')}
          >
            <option value="">{needsDossier ? 'Choisir un dossier (obligatoire)' : 'Sans dossier'}</option>
            {(dossiers ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.number ? `${d.number} — ${d.title}` : d.title}
              </option>
            ))}
          </select>
        )}
        <PersonSelect
          fluid
          label="intervenants"
          emptyLabel={kind === 'task' ? 'Personne (non assignée)' : 'Choisir les intervenants'}
          value={people}
          onChange={setPeople}
        />
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-neutral-100 px-3.5 py-2.5">
        <button
          type="button"
          onClick={() => onMore(kind)}
          className="rounded-full px-2.5 py-1.5 text-xs font-medium text-cime-text-muted hover:bg-neutral-100 hover:text-cime-text-primary"
        >
          Plus d’options
        </button>
        <span className="min-w-0 flex-1 truncate text-right text-xs text-[#c8431f]">{title.trim() ? missing : ''}</span>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!!missing || saving}
          className="h-8 rounded-full bg-cime-primary px-3.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-40"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
